import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheck, FaExclamationTriangle, FaArrowRight } from "react-icons/fa";
import { inviteAcceptApi } from "../../services/apiCall";
import { screenPath } from "../../constant/strings";
import "./invite_accept.css";

const InviteAccept = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("Verifying your invitation...");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    console.log("InviteAccept mounted with token:", token);
    if (!token) {
      setStatus("error");
      setMessage("Invalid invitation link. No token found.");
      return;
    }

    let timer;
    const acceptInvitation = async () => {
      try {
        console.log("Calling inviteAcceptApi...");
        await inviteAcceptApi({ token });
        setStatus("success");
        setMessage("Your invitation has been accepted successfully!");
        
        // Start progress bar for redirect
        const duration = 3000;
        const interval = 10;
        const step = (interval / duration) * 100;
        
        let currentProgress = 0;
        timer = setInterval(() => {
          currentProgress += step;
          setProgress(Math.min(currentProgress, 100));
          if (currentProgress >= 100) {
            clearInterval(timer);
            navigate(screenPath.signup);
          }
        }, interval);
      } catch (error) {
        console.error("Invitation acceptance failed:", error);
        setStatus("error");
        setMessage(error.response?.data?.message || "Failed to accept invitation. It may have expired.");
      }
    };

    acceptInvitation();
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [token, navigate]);

  return (
    <div className="invite-accept-container">
      <motion.div 
        className="invite-accept-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <AnimatePresence mode="wait">
          {status === "loading" && (
            <motion.div 
              key="loading"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="status-icon-wrapper"
            >
              <div className="loading-spinner"></div>
            </motion.div>
          )}

          {status === "success" && (
            <motion.div 
              key="success"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="status-icon-wrapper"
            >
              <div className="success-icon">
                <FaCheck />
              </div>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div 
              key="error"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="status-icon-wrapper"
            >
              <div className="error-icon">
                <FaExclamationTriangle />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <h1 className="invite-accept-title">
          {status === "loading" ? "Processing..." : 
           status === "success" ? "Invitation Accepted" : "Acceptance Failed"}
        </h1>

        <p className="invite-accept-message">{message}</p>

        {status === "success" && (
          <div className="redirect-info">
            <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              Redirecting you to complete your signup...
            </p>
            <div className="redirect-progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {status === "error" && (
          <button 
            className="btn-retry" 
            onClick={() => navigate(screenPath.login)}
          >
            Go to Login <FaArrowRight style={{ marginLeft: '8px' }} />
          </button>
        )}
      </motion.div>
    </div>
  );
};

export default InviteAccept;
